// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

// External libraries
import { isEqual } from 'lodash';

// Project libraries
import { ProfileCache, ProfileData, SelectorOrganization, UserData } from '@tgx/shared/interfaces';
import { FormValidator, getHeaderMenuUserName } from '@tgx/shared/utils';
import { ToastsService } from '@tgx/shared/toasts';

// Local imports
import { OnboardingService } from '../../services/onboarding.service';
import { Steps } from '../../enum/steps';
import { CacheService, PlatformInfoService, PlatformSelectorOrganizationService } from '@tgx/shared/services';

@Component({
  selector: 'tgx-onboarding-company-professional-details',
  templateUrl: './onboarding-company-professional-details.component.html',
  styleUrls: ['./onboarding-company-professional-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OnboardingCompanyProfessionalDetailsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private onboardingService = inject(OnboardingService);
  private toast = inject(ToastsService);
  private platformSelectorOrganizationService = inject(PlatformSelectorOrganizationService);
  private cacheService = inject(CacheService);
  private platformInfoService = inject(PlatformInfoService);

  @Input() loggedUser: UserData;
  @Input() jobTitle: string;
  @Input() companyName: string;
  @Input() selectedOrg: SelectorOrganization;
  @Output() changeStep = new EventEmitter<Steps>();

  professionalDetailsForm: FormGroup;
  originalFormData = FormGroup;

  buttonName = 'Save & continue';
  isLoading = false;
  isEditing = false;
  isInitialValue = true;
  userName: string;
  jobTitleBackup: string;
  companyNameBackup: string;
  userProfileSuccess = false;
  orgProfileSuccess = false;

  ngOnInit() {
    this.buildProfessionalDetailsForm();
    this.isEditing = this.onboardingService.getIsEditState();
    this.userName = getHeaderMenuUserName(this.loggedUser);
    this.jobTitleBackup = this.jobTitle;
    this.companyNameBackup = this.companyName;

    if (this.companyName) {
      this.professionalDetailsForm.controls['companyName'].setValue(this.companyName);
      this.professionalDetailsForm.get('companyName')?.markAsDirty();
    }
    if (this.jobTitle) {
      this.professionalDetailsForm.controls['jobTitle'].setValue(this.jobTitle);
      this.professionalDetailsForm.get('jobTitle')?.markAsDirty();
    }

    if (this.isEditing) {
      this.buttonName = 'Update';
      this.originalFormData = this.professionalDetailsForm.getRawValue();

      this.professionalDetailsForm.valueChanges.subscribe(() => {
        const currentFormValue = this.professionalDetailsForm.getRawValue();
        this.isInitialValue = isEqual(currentFormValue, this.originalFormData);
      });
    }
  }

  isButtonDisabled(): boolean {
    const isFormValid = this.professionalDetailsForm.valid;

    if (this.isEditing) {
      return !isFormValid || this.isInitialValue;
    }
    return !isFormValid || this.isLoading;
  }

  private buildProfessionalDetailsForm() {
    this.professionalDetailsForm = this.fb.group({
      companyName: this.fb.control('', [
        FormValidator.required('company name'),
        FormValidator.checkInputValue('company name'),
        FormValidator.invalidCharWithAmpersand('company name'),
        FormValidator.maxLength('company name', 100),
      ]),
      jobTitle: this.fb.control('', [
        FormValidator.required('job title'),
        FormValidator.textNumbers('job title'),
        FormValidator.checkInputValue('job title'),
        FormValidator.maxLength('job title', 50),
        FormValidator.textInvalidChar('job title'),
      ]),
    });
  }

  getValidationStatus(controlName: string, isRequired: boolean): string {
    return this.onboardingService.getValidationStatus(this.professionalDetailsForm, controlName, isRequired);
  }

  async submitDetailsForm() {
    this.isLoading = true;

    const professionalDetailsFinishedAt = this.isEditing ? null : new Date().toISOString();

    const [userProfiles, orgProfiles] = await this.executeUpdates(professionalDetailsFinishedAt);

    this.userProfileSuccess = userProfiles.status === 'fulfilled';
    this.orgProfileSuccess = orgProfiles.status === 'fulfilled';

    this.manageSuccessResult(
      orgProfiles.status === 'fulfilled' ? orgProfiles.value : null,
      professionalDetailsFinishedAt,
    );

    if (this.userProfileSuccess && this.orgProfileSuccess) {
      if (this.isEditing) {
        this.toast.addToast('Professional details updated successfully', 'bg-success');
        this.changeStep.emit(Steps.REVIEW);
      } else {
        this.changeStep.emit(Steps.COMPANY_CONTACTS);
      }
    } else {
      this.manageErrors(userProfiles, orgProfiles);
    }

    this.isLoading = false;
  }

  private async executeUpdates(
    professionalDetailsFinishedAt: string | null,
  ): Promise<[PromiseSettledResult<string>, PromiseSettledResult<ProfileData>]> {
    const jobTitle = this.professionalDetailsForm.controls['jobTitle'].value;

    const updateMemberData = this.onboardingService.updateMemberDataInOrganization(
      this.loggedUser.email,
      this.selectedOrg.code,
      {
        jobTitle,
      },
    );

    const updateOnboardingDate =
      this.isEditing || !professionalDetailsFinishedAt
        ? Promise.resolve()
        : this.onboardingService.updateProfessionalDetailsFinishedAt(
            this.loggedUser.email,
            professionalDetailsFinishedAt,
          );

    const updateUserData = Promise.all([updateMemberData, updateOnboardingDate]).then(([memberData]) => memberData);

    const updateOrgProfile = this.onboardingService.updateProfile(
      this.selectedOrg.code,
      this.professionalDetailsForm.controls['companyName'].value,
    );

    return await Promise.allSettled([updateUserData, updateOrgProfile]);
  }

  private async manageSuccessResult(
    profileData: ProfileData,
    professionalDetailsFinishedAt: string | null,
  ): Promise<void> {
    if (this.userProfileSuccess) {
      const jobTitle = this.professionalDetailsForm.controls['jobTitle'].value;
      const partnersIncludedIn = this.loggedUser.partnersIncludedIn || [];
      const hasSelectedOrg = partnersIncludedIn.some((partner) => partner.organization.code === this.selectedOrg.code);
      const updatedPartners = hasSelectedOrg
        ? partnersIncludedIn.map((partner) =>
            partner.organization.code === this.selectedOrg.code ? { ...partner, jobTitle } : partner,
          )
        : [...partnersIncludedIn, { organization: { code: this.selectedOrg.code }, jobTitle }];

      const onboardingProperties = {
        ...(this.loggedUser.onboardingProperties || {}),
        ...(professionalDetailsFinishedAt ? { professionalDetailsFinishedAt } : {}),
      };

      this.platformInfoService.userLogged$.next({
        ...this.loggedUser,
        hasUserProfile: true,
        partnersIncludedIn: updatedPartners,
        onboardingProperties: onboardingProperties as any,
      });
    } else {
      this.professionalDetailsForm.controls['jobTitle'].setValue(this.jobTitleBackup || '');
    }

    if (this.orgProfileSuccess) {
      this.updateProfileCache(profileData);
    } else {
      this.professionalDetailsForm.controls['companyName'].setValue(this.companyNameBackup);
    }
  }

  private manageErrors(userProfiles: any, orgProfiles: any): void {
    const messages = [];

    if (!this.userProfileSuccess) {
      messages.push(`• job title: ${userProfiles.reason?.message || userProfiles.reason}`);
    }

    if (!this.orgProfileSuccess) {
      messages.push(`• company name: ${orgProfiles.reason?.message || orgProfiles.reason}`);
    }
    this.toast.addToast('Error updating professional details', 'bg-danger', messages.join('<br/>'));
  }

  private updateProfileCache(profileData: ProfileData): void {
    this.cacheService
      .retrieveProfilesByCodes([this.selectedOrg.code])
      .then(async (profileCache) => {
        const profileCacheChanged: ProfileCache = {
          ...profileCache[0],
          name: profileData.name,
        };
        this.cacheService.updateProfileCached(this.selectedOrg.code, profileCacheChanged);
        const org = await this.platformSelectorOrganizationService.getOrganization(this.selectedOrg.code);
        this.platformInfoService.organizationSelected$.next(org);
      })
      .catch((err) => {
        this.toast.addToast('No profile retrieve', 'bg-danger', err);
      });
  }

  cancelChanges() {
    this.changeStep.emit(Steps.REVIEW);
  }
}
